import {BehaviorSubject, Observable} from 'rxjs';
import {Controller} from '../Controller';
import {Bound} from '../../common/decorators/methods/Bound';
import {Throttled} from '../../common/decorators/methods/Throttled';
import {tAsString} from '../../common/helpers/react/text/tAsString';
import {HistoryEntry} from '../../common/model/types/history/HistoryEntry';
import {THROTTLING_INTERVAL_FOR_HTTP, TIMEOUT_FOR_INSTANT_ACTION} from '../../common/CONST';
import {TableSortingState} from '../../common/model/types/sorting/TableSortingState';
import {SortOrder} from '../../core/rest-client/aat';

export interface HistoryPagingState {
    page: number;
    pageSize: number;
    totalRecords: number;
}

const DEFAULT_PAGE_SIZE = 10;

export class HistoryController extends Controller {
    #history = new BehaviorSubject<HistoryEntry[]>([]);
    #historySortingState = new BehaviorSubject<TableSortingState>({
        sortBy: 'dateTime',
        sortOrder: SortOrder.Desc
    });
    #historyPagingState$ = new BehaviorSubject<HistoryPagingState>({
        page: 0,
        pageSize: DEFAULT_PAGE_SIZE,
        totalRecords: 0
    });

    getHistory$(): Observable<HistoryEntry[]> {
        return this.#history;
    }

    getHistorySortingState$(): Observable<TableSortingState> {
        return this.#historySortingState;
    }

    getHistoryPagingState$(): Observable<HistoryPagingState> {
        return this.#historyPagingState$;
    }

    async changeHistorySorting(sortBy?: string, sortOrder?: SortOrder): Promise<void> {
        this.#historySortingState.next({sortBy, sortOrder});
        await this.reloadAllHistory();
    }

    async loadPage(page: number): Promise<void> {
        this.#history.next([]);
        await this.reloadAllHistory(page);
    }

    async changePageSize(pageSize: number): Promise<void> {
        this.#historyPagingState$.next({
            ...this.#historyPagingState$.value,
            page: 0,
            pageSize
        });

        await this.loadPage(0);
    }

    @Bound()
    @Throttled({
        recall: true,
        timeout: THROTTLING_INTERVAL_FOR_HTTP + TIMEOUT_FOR_INSTANT_ACTION
    })
    async refreshHistory(): Promise<void> {
        await this.reloadAllHistory();
    }

    private async reloadAllHistory(
        page = this.#historyPagingState$.value.page
    ): Promise<void> {
        const {pageSize} = this.#historyPagingState$.value;
        const {sortBy, sortOrder} = this.#historySortingState.value;

        const response = await this.getBusyFlag().run(
            () => this.services.history.getHistory(
                page,
                pageSize,
                sortBy,
                sortOrder
            ),
            tAsString('REFRESHING_HISTORY')
        );

        this.#history.next(response.historyEntries);
        this.#historyPagingState$.next({
            page: response.page,
            pageSize: response.pageSize,
            totalRecords: response.totalRecords
        });
    }
}
